import React from "react";
import { Link } from "react-router-dom";
import { getShell, isNativeWebView } from "../lib/apiKey";
import { BrandMark, Mark } from "../design/primitives";
import { LEGAL_NAV, NavItem, PRIMARY_NAV, SECONDARY_NAV, visibleItems } from "../design/nav";
import { hasNativeChrome } from "../design/theme";
import { LanguageSelect, ThemeToggle } from "./Header";

function FooterLink({ item }: { item: NavItem }) {
  const cls = "inline-flex min-h-[32px] items-center text-sm text-ev-muted hover:text-ev-fg hover:underline hover:underline-offset-4";
  if (item.href) {
    return (
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={cls}>
        {item.label}&nbsp;<span aria-hidden="true">↗</span>
      </a>
    );
  }
  return (
    <Link to={item.to!} className={cls}>
      {item.label}
    </Link>
  );
}

/**
 * Site footer: the full sitemap in a ruled grid, then legal.
 *
 * Apple native shells never show the Shop link (App Review 3.1.1: the
 * shop sells physical prints, allowed under 3.1.3(e), but reviewers
 * conflate the WebView entry with digital content). The EULA link stays
 * everywhere (3.1.2(c) requires it in-app). Shells with native chrome get
 * only the compact legal row.
 */
export default function Footer() {
  const shell = getShell();
  const native = isNativeWebView();
  const compact = hasNativeChrome();
  const legal = (
    <ul className="flex flex-wrap gap-x-5 gap-y-1" aria-label="Legal">
      {LEGAL_NAV.map((it) => (
        <li key={it.label}>
          <FooterLink item={it} />
        </li>
      ))}
      <li>
        <FooterLink item={{ label: "Support", to: "/support" }} />
      </li>
    </ul>
  );

  if (compact) {
    return (
      <footer className="bg-ev-bg px-4 pb-8 pt-6 text-ev-fg" style={{ borderTop: "var(--ev-rule-w) solid var(--ev-rule)" }}>
        {legal}
        <div className="mt-4">
          <LanguageSelect id="ev-footer-lang" />
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-ev-bg text-ev-fg" style={{ borderTop: "var(--ev-rule-w-thick) solid var(--ev-rule)" }}>
      <div className="mx-auto max-w-ev px-4 py-12 lg:px-10">
        <div className="grid gap-10 lg:grid-cols-[1.2fr_repeat(5,1fr)]">
          <div>
            <Link to="/" className="inline-flex items-center gap-3" aria-label="ElectronVisualized, home">
              <BrandMark size={14} />
              <span className="text-lg font-semibold tracking-tight">ElectronVisualized</span>
            </Link>
            <p className="ev-small mt-3 max-w-xs">
              Electron density and orbitals from real DFT. Designed and developed by John Seong.
            </p>
          </div>
          {PRIMARY_NAV.map((g) => (
            <nav key={g.id} aria-labelledby={`foot-${g.id}`}>
              <h2 id={`foot-${g.id}`} className="mb-3 flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.14em] text-ev-fg">
                <Mark glyph={g.glyph} accent={g.accent} size={9} />
                {g.label}
              </h2>
              <ul>
                {visibleItems(g.items, shell, native).map((it) => (
                  <li key={it.label}>
                    <FooterLink item={it} />
                  </li>
                ))}
              </ul>
            </nav>
          ))}
        </div>

        <div className="ev-hairline mt-12 flex flex-wrap items-center justify-between gap-6 pt-6">
          <ul className="flex flex-wrap gap-x-5 gap-y-1" aria-label="More">
            {visibleItems(SECONDARY_NAV, shell, native).map((it) => (
              <li key={it.label}>
                <FooterLink item={it} />
              </li>
            ))}
          </ul>
          <div className="flex flex-wrap items-center gap-6">
            <LanguageSelect id="ev-footer-lang" />
            <ThemeToggle />
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          {legal}
          <p className="text-sm text-ev-muted">© {new Date().getFullYear()} ElectronVisualized | John Wonmo Seong</p>
        </div>
      </div>
    </footer>
  );
}
